"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Warning, Clock, CurrencyDollar, Package, ArrowLeft } from "@phosphor-icons/react";
import { Loader2, RefreshCcw, ShieldAlert } from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import axios from "axios";
import { numberWithCommas } from "@/lib/formatCommas";
import { cn } from "@/lib/utils";
import { useSelector } from "react-redux";

const baseUrl = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1/";

const MetricCard = ({ title, value, subtitle, icon: Icon, color = "blue" }) => {
    const colors = {
        blue: "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400",
        red: "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400",
        orange: "bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400",
        green: "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400",
    };
    return (
        <Card className="bg-white dark:bg-slate-900 border shadow-sm h-full">
            <CardContent className="p-4">
                <div className="flex items-start justify-between">
                    <div className="min-w-0">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{title}</p>
                        <p className="text-2xl font-bold mt-1 truncate">{value}</p>
                        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
                    </div>
                    <div className={cn("p-2 rounded-lg shrink-0", colors[color])}>
                        <Icon className="h-5 w-5" weight="duotone" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

const StatusBadge = ({ status, daysLeft }) => {
    if (status === "expired")
        return <Badge className="bg-red-600 hover:bg-red-700">Expired {Math.abs(daysLeft)}d ago</Badge>;
    if (status === "critical")
        return <Badge className="bg-orange-500 hover:bg-orange-600">{daysLeft}d left</Badge>;
    return <Badge variant="outline">{daysLeft}d left</Badge>;
};

export default function ExpiringStockReportPage() {
    const { user } = useSelector((state) => state.auth);
    const [isLoading, setIsLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [days, setDays] = useState("90");

    const currency = user?.shop?.currency || "GHS";

    const hasPermission = () => {
        if (!user?.role?.permissions) return false;
        const perms = typeof user.role.permissions === "string"
            ? JSON.parse(user.role.permissions) : user.role.permissions;
        return perms.includes("ViewReports") || perms.includes("Reports") ||
            perms.includes("Inventory") || perms.includes("Stock") || perms.includes("Settings");
    };

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const res = await axios.get(`${baseUrl}analytics/expiring-stock`, {
                params: { days },
                withCredentials: true,
            });
            if (res.data.success) {
                setItems(res.data.data?.items || []);
                setSummary(res.data.data?.summary || null);
            }
        } catch (e) {
            console.error("Failed to fetch expiring stock");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days]);

    if (!hasPermission()) {
        return (
            <Card className="mx-auto max-w-md mt-20">
                <CardContent className="pt-6 text-center">
                    <ShieldAlert className="h-12 w-12 mx-auto text-red-500 mb-4" />
                    <h2 className="text-xl font-bold mb-2">Access Denied</h2>
                    <p className="text-muted-foreground mb-4">You don't have permission to view reports.</p>
                    <Link to="/dashboard"><Button>Go to Dashboard</Button></Link>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="mx-auto p-4 space-y-6">
            <motion.div className="flex flex-wrap justify-between items-center gap-4"
                initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
                <div className="flex items-center gap-4">
                    <Link to="/dashboard/reports">
                        <Button variant="outline" size="icon"><ArrowLeft className="h-4 w-4" /></Button>
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold">Expiring Stock</h1>
                        <p className="text-sm text-muted-foreground">Batches expired or nearing expiry</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Label className="text-sm">Window</Label>
                    <Select value={days} onValueChange={setDays}>
                        <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="30">Next 30 days</SelectItem>
                            <SelectItem value="60">Next 60 days</SelectItem>
                            <SelectItem value="90">Next 90 days</SelectItem>
                            <SelectItem value="180">Next 180 days</SelectItem>
                            <SelectItem value="365">Next 365 days</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={fetchData} disabled={isLoading}>
                        <RefreshCcw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />Refresh
                    </Button>
                </div>
            </motion.div>

            {isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <MetricCard title="Expired Batches" value={numberWithCommas(summary?.expiredBatches || 0)}
                            subtitle={`${numberWithCommas(summary?.expiredUnits || 0)} units`} icon={Warning} color="red" />
                        <MetricCard title="Expiring Soon" value={numberWithCommas(summary?.expiringBatches || 0)}
                            subtitle={`within ${days} days`} icon={Clock} color="orange" />
                        <MetricCard title="Value at Risk" value={`${currency} ${numberWithCommas((summary?.valueAtRisk || 0).toFixed(2))}`}
                            subtitle="at cost" icon={CurrencyDollar} color="blue" />
                        <MetricCard title="Batches Listed" value={numberWithCommas(items.length)}
                            subtitle="soonest first" icon={Package} color="green" />
                    </div>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Batches</CardTitle>
                            <CardDescription>Expired and soon-to-expire stock, ordered by expiry date</CardDescription>
                        </CardHeader>
                        <div className="p-0 overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Product</TableHead>
                                        <TableHead>Batch</TableHead>
                                        <TableHead className="text-right">Qty</TableHead>
                                        <TableHead>Expiry</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="text-right">Value</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {items.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} className="text-center h-32 text-muted-foreground">
                                                Nothing expiring within this window. 🎉
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        items.map((it) => (
                                            <TableRow key={it.stockId} className={it.status === "expired" ? "bg-red-50/50 dark:bg-red-900/10" : ""}>
                                                <TableCell className="font-medium">{it.productName}</TableCell>
                                                <TableCell className="text-muted-foreground">{it.batchNumber || "—"}</TableCell>
                                                <TableCell className="text-right">{numberWithCommas(it.quantity)}</TableCell>
                                                <TableCell>{it.expiryDate ? format(new Date(it.expiryDate), "dd MMM yyyy") : "—"}</TableCell>
                                                <TableCell><StatusBadge status={it.status} daysLeft={it.daysLeft} /></TableCell>
                                                <TableCell className="text-right">{currency} {numberWithCommas((it.value || 0).toFixed(2))}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </Card>
                </>
            )}
        </div>
    );
}
